import {
  configApiRef,
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Container, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AsyncState } from '../../components/AsyncState';

const useStyles = makeStyles(theme => ({
  gate: {
    marginTop: theme.spacing(10),
  },
  gatePanel: {
    padding: theme.spacing(4),
  },
  gateBody: {
    marginTop: theme.spacing(2),
  },
}));

export const PORTAL_PERMISSION_KEYS = [
  'catalog.read',
  'templates.execute',
  'repositories.read',
  'ecosystem.read',
  'gcp.operations.read',
  'gcp.recommendations.read',
  'gcp.resources.classify',
  'mappings.manage',
  'repositories.autofix.preview',
  'repositories.autofix.apply',
  'repositories.autofix.approve',
  'repositories.autofix.kill-switch.manage',
  'audit.read',
  'audit.export',
  'policy.read',
  'policy.manage',
  'runtime-config.manage',
  'access.manage',
] as const;

export type PortalPermission = (typeof PORTAL_PERMISSION_KEYS)[number];
export type PortalRole =
  | 'administrador'
  | 'operador'
  | 'leitor'
  | 'personalizado';
export type PortalPermissions = Record<PortalPermission, boolean>;

export type PortalAccess = {
  userEntityRef: string;
  enabled: boolean;
  role: PortalRole;
  permissions: PortalPermissions;
  source: 'bootstrap' | 'assigned' | 'team' | 'unassigned';
  teams?: Array<{ id: number; name: string }>;
  updatedBy: string | null;
  updatedAt: string | null;
};

export function permissionsForRole(role: PortalRole): PortalPermissions {
  const permissions = Object.fromEntries(
    PORTAL_PERMISSION_KEYS.map(key => [key, role === 'administrador']),
  ) as PortalPermissions;
  if (role === 'operador') {
    for (const key of PORTAL_PERMISSION_KEYS) {
      permissions[key] = ![
        'access.manage',
        'gcp.resources.classify',
        'repositories.autofix.approve',
        'repositories.autofix.kill-switch.manage',
        'audit.export',
        'policy.manage',
        'runtime-config.manage',
      ].includes(key);
    }
  }
  if (role === 'leitor') {
    for (const key of [
      'catalog.read',
      'repositories.read',
      'ecosystem.read',
      'gcp.operations.read',
      'gcp.recommendations.read',
      'repositories.autofix.preview',
      'policy.read',
    ] as PortalPermission[]) {
      permissions[key] = true;
    }
  }
  return permissions;
}

const AccessContext = createContext<PortalAccess | undefined>(undefined);

export function usePortalAccess() {
  return useContext(AccessContext);
}

export function PortalAccessGate({ children }: { children: ReactNode }) {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [access, setAccess] = useState<PortalAccess>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        if (configApi.getString('auth.environment') === 'development') {
          const permissions = permissionsForRole('administrador');
          if (active) {
            setAccess({
              userEntityRef: 'user:development/guest',
              enabled: true,
              role: 'administrador',
              permissions,
              source: 'bootstrap',
              updatedBy: null,
              updatedAt: null,
            });
          }
          return;
        }
        const [{ token }, baseUrl] = await Promise.all([
          identityApi.getCredentials(),
          discoveryApi.getBaseUrl('access-control'),
        ]);
        const response = await fetch(`${baseUrl}/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok)
          throw new Error(`A consulta respondeu ${response.status}`);
        const result = (await response.json()) as PortalAccess;
        if (active) setAccess(result);
      } catch (loadError) {
        if (active) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Não foi possível validar seu acesso.',
          );
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [configApi, discoveryApi, identityApi]);

  // Carregamento e erro passam pelo mesmo padrão do resto do portal, dentro do
  // enquadramento centrado que o portão já usava. O "tentar novamente" continua
  // recarregando a página, como antes.
  if (error || !access) {
    return (
      <Container maxWidth="sm" className={classes.gate}>
        <Paper className={classes.gatePanel}>
          <AsyncState
            loading={!access && !error}
            error={error}
            data={access}
            onRetry={() => window.location.reload()}
            variant="inline"
            loadingLabel="Validando seu acesso…"
          >
            {() => null}
          </AsyncState>
        </Paper>
      </Container>
    );
  }

  if (!access.enabled) {
    return (
      <Container maxWidth="sm" className={classes.gate}>
        <Paper className={classes.gatePanel}>
          <Typography variant="h4" component="h1">
            Acesso pendente
          </Typography>
          <Typography className={classes.gateBody}>
            Sua conta corporativa foi autenticada, mas ainda não recebeu acesso
            ao Portal DevOps. Solicite a liberação a um administrador do portal.
          </Typography>
          <Typography color="textSecondary" className={classes.gateBody}>
            Identidade: {access.userEntityRef}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <AccessContext.Provider value={access}>{children}</AccessContext.Provider>
  );
}
